import { ModalSimpleMessageService } from 'app/shared/modals/modal-simple-message.service';

import { isJestRunning } from '..';
import { AppModuleServices } from '../app-module-util';
import { HelpCenterUrls } from '../help-center';
import { Loggable } from '../logging/loggable';

const createDummyVideoStream = ({
  height = 220,
  width = 220,
}: { width?: number; height?: number } = {}): MediaStream => {
  // None of this is supported in jsdom, just punt
  if (isJestRunning()) {
    return {} as MediaStream;
  }
  const canvas = document.createElement('canvas');
  canvas.id = 'dummy-video-stream';
  canvas.width = width;
  canvas.height = height;

  const context = canvas.getContext('2d');
  if (!context) {
    throw new Error('Could not create canvas context');
  }
  context.fillRect(0, 0, width, height);

  const stream = canvas.captureStream();
  const streamTrack = stream.getVideoTracks()[0];
  streamTrack.enabled = false;

  canvas.remove();

  return new MediaStream([streamTrack]);
};

export const DUMMY_VIDEO_STREAM = createDummyVideoStream();

const streams: Record<string, MediaStream> = {};

export const cleanTracks = (stream: MediaStream | undefined, loggable?: Loggable): Promise<void> => {
  if (!stream) {
    return Promise.resolve();
  }
  try {
    stream.getTracks().forEach((t) => t.stop());
    loggable?.info('cleanTracks', { streamId: stream.id });
    delete streams[stream.id];
    return Promise.resolve();
  } catch (e: unknown) {
    loggable?.error('Error Cleaning Stream Tracks: ', e);
    return Promise.reject(e);
  }
};

let errorState = false;

export const getUserMedia = async (
  constraints: MediaStreamConstraints | undefined,
  loggable?: Loggable,
): Promise<MediaStream> => {
  try {
    const stream = await navigator.mediaDevices.getUserMedia(constraints);
    loggable?.info('getUserMedia', { constraints, streamId: stream.id });
    streams[stream.id] = stream;
    return stream;
  } catch (e) {
    if (e instanceof Error) {
      // Check if 'e' is an instance of Error
      if (e.name === 'NotAllowedError' && !errorState) {
        errorState = true;
      }
      loggable?.error('Error Accessing Media Stream: ', e.message);
    } else {
      loggable?.error('An unexpected error occurred:', e);
    }
    throw e;
  }
};

export const showPermissionModal = (): void => {
  const modalOpen = AppModuleServices.injector.get(ModalSimpleMessageService).checkIfModalOpen();
  if (!modalOpen) {
    AppModuleServices.injector.get(ModalSimpleMessageService).throw(
      {
        title: 'Allow Scoot to use your camera and microphone',
        message: `Scoot needs to access your camera and microphone so others can hear you and see you. You will need to confirm this on each browser. <a href="${HelpCenterUrls.SCOOT_ENABLE_CAMERA_AND_MIC}" target="_blank">Learn more</a>`,
        actionButtonTitle: 'Dismiss',
        customModalClass: 'custom-width-warning-modal-small',
        showClose: false,
      },
      true,
      false,
      true,
    );
  }
};

export const closePermissionModal = (): void => {
  const modalOpen = AppModuleServices.injector.get(ModalSimpleMessageService).checkIfModalOpen();
  if (modalOpen) {
    AppModuleServices.injector.get(ModalSimpleMessageService).close();
  }
};

export const getActiveStreams = (): Record<string, MediaStream> => {
  return streams;
};

export const cleanAllStreams = async (): Promise<void> => {
  await Promise.all(Object.keys(streams).map((id) => cleanTracks(streams[id])));
};
